import React from 'react';

import { CircularProgress, Stack, Typography, SxProps, styled } from '@mui/material';

import palette from '../theme/palette';

type Size = 'small' | 'medium' | 'large';

const sizeStyles = {
  small: {
    height: '28px',
    padding: '4px 10px',
    fontSize: '11px'
  },
  medium: {
    height: '36px',
    padding: '8px 12px',
    fontSize: '12px'
  },
  large: {
    height: '44px',
    padding: '12px',
    fontSize: '13px'
  }
};

const Container = styled(Stack, {
  shouldForwardProp: prop => prop !== 'loading' && prop !== 'disabled' && prop !== 'size'
})<{
  loading: number;
  disabled: boolean;
  size: Size;
}>(({ loading, disabled, size }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '20px',
  gap: '4px',
  width: '100%',
  cursor: disabled || loading ? 'not-allowed' : 'pointer',
  opacity: disabled || loading ? 0.4 : 1,
  background: palette.black,
  height: sizeStyles[size].height,
  padding: sizeStyles[size].padding,
  ...(!disabled &&
    !loading && {
      '&:active': {
        opacity: 0.8
      }
    })
}));

const Text = styled(Typography)<{ size: Size }>(({ size }) => ({
  color: palette.white,
  fontFamily: 'Reddit Sans',
  fontWeight: 600,
  fontSize: sizeStyles[size].fontSize
}));

type ButtonProps = React.HTMLAttributes<HTMLDivElement> & {
  text?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  sx?: SxProps;
  textSx?: SxProps;
  disabled?: boolean;
  loading?: boolean;
  size?: Size;
  [key: string]: unknown;
};

const Button = ({
  text,
  onClick,
  sx,
  textSx,
  disabled = false,
  loading = false,
  size = 'medium',
  ...props
}: ButtonProps) => (
  <Container
    tabIndex={-1}
    sx={sx}
    loading={+loading}
    disabled={disabled}
    size={size}
    onClick={onClick}
    {...props}
  >
    {!loading ? (
      <Text sx={textSx} size={size}>
        {text}
      </Text>
    ) : (
      <CircularProgress sx={{ color: palette.white }} size="20px" />
    )}
  </Container>
);

export default Button;
